import { getEnvironment } from "@p0security/frontend-core";
import type { AgenticIntegration } from "@p0security/integrations-agentic-shared/components";
import {
  gatewayOauthEndpointOf,
  gatewayUrlOf,
} from "@p0security/integrations-agentic-shared/domain-hosting";
import {
  installedIds,
  installedItems,
} from "@p0security/shared/install/installed";
import { compact } from "lodash-es";

import type { Delegate } from "./DelegationChain";
import { urlOf } from "./url";

/** An installed component a gateway hosts: an MCP server or an A2A bridge. */
export type GatewayHostedComponent = "a2a-bridge" | "server";

/** Installed agentic components that a delegation-chain identity can name. */
export type AgenticInstalls = {
  /** IDs of the installed items of each component. */
  componentIds: Record<GatewayHostedComponent, ReadonlySet<string>>;
  /** Host of every installed gateway, from both its URL and its OAuth
   * endpoint. A gateway can mint its tokens at an endpoint on a different host
   * than the gateway, so an issuer matches either one. */
  gatewayHosts: ReadonlySet<string>;
};

export const EMPTY_AGENTIC_INSTALLS: AgenticInstalls = {
  componentIds: { "a2a-bridge": new Set(), server: new Set() },
  gatewayHosts: new Set(),
};

/** What issued a delegation-chain identity, and what it points at.
 *
 * A `user` carries no issuer: it is the subject the chain acted for, which the
 * chain itself names rather than any token. `isInstalled` is false when the
 * gateway hosts a subject that has no install item, such as its built-in P0 and
 * A2A access servers.
 */
export type GatewayHostedIdentity = {
  id: string;
  isInstalled: boolean;
  type: GatewayHostedComponent;
};

export type DelegateIdentity =
  | GatewayHostedIdentity
  | { type: "client" }
  | { type: "unknown" }
  | { type: "user" };

/** Whether a delegate names a component that a gateway hosts. */
export const isGatewayHosted = (
  identity: DelegateIdentity
): identity is GatewayHostedIdentity => "id" in identity;

const GATEWAY_ROUTES = {
  a2a: "a2a-bridge",
  mcps: "server",
} as const satisfies Record<string, GatewayHostedComponent>;

// The gateway owns the route names and the shape `<route>/<id>/<hash>`, and it
// lives in another repo.
// TODO: Derive both from a shared source; a silent change there drops these
// links.
const GATEWAY_SUBJECT_PATTERN = new RegExp(
  `^/?(${Object.keys(GATEWAY_ROUTES).join("|")})/([^/]+)/[^/]+$`
);

const isGatewayRoute = (route?: string): route is keyof typeof GATEWAY_ROUTES =>
  route !== undefined && route in GATEWAY_ROUTES;

const hostOf = (value?: string | null) => urlOf(value)?.host;

/** Hostnames that name the machine the app runs on, in local development. */
const LOCAL_HOSTNAMES = ["host.docker.internal", "localhost"];

/** Whether an issuer host is the P0 controller.
 *
 * A gateway running in a container reaches a locally served controller through
 * the docker host alias, so in local development its issuer names a different
 * host than the configured API URL, on the same port.
 */
const isControllerHost = (host: string) => {
  const apiUrl = urlOf(getEnvironment().apiUrl);
  if (!apiUrl) return false;
  if (host === apiUrl.host) return true;
  if (!apiUrl.port || !LOCAL_HOSTNAMES.includes(apiUrl.hostname)) return false;
  return LOCAL_HOSTNAMES.some(
    (hostname) => host === `${hostname}:${apiUrl.port}`
  );
};

/** Collects the installed components a delegation chain can link to. */
export const toAgenticInstalls = (
  config?: AgenticIntegration
): AgenticInstalls => {
  if (!config) return EMPTY_AGENTIC_INSTALLS;
  const gateways = Object.values(installedItems("gateway", config));
  return {
    componentIds: {
      "a2a-bridge": new Set(installedIds("a2a-bridge", config)),
      server: new Set(installedIds("server", config)),
    },
    gatewayHosts: new Set(
      compact(
        gateways.flatMap((gateway) => [
          hostOf(gatewayUrlOf(gateway)),
          hostOf(gatewayOauthEndpointOf(gateway)),
        ])
      )
    ),
  };
};

/** Identifies a delegation-chain entry from its issuer and subject.
 *
 * P0 issues client identities, so their subject is a registered client ID. An
 * installed gateway issues its own servers and bridges, and names them with the
 * subject `<route>/<installed component ID>/<hash>`.
 */
export const identityOfDelegate = (
  { iss, sub }: Delegate,
  installs: AgenticInstalls
): DelegateIdentity => {
  if (!iss) return { type: "user" };
  const issuerHost = hostOf(iss);
  if (!issuerHost) return { type: "unknown" };
  if (isControllerHost(issuerHost)) return { type: "client" };
  if (!installs.gatewayHosts.has(issuerHost)) return { type: "unknown" };
  const [, route, id] = GATEWAY_SUBJECT_PATTERN.exec(sub) ?? [];
  if (!isGatewayRoute(route) || !id) return { type: "unknown" };
  const type = GATEWAY_ROUTES[route];
  return { id, isInstalled: installs.componentIds[type].has(id), type };
};
